"use client";

import { useKioskEvent } from "@/app/directories/kiosk/contexts/KioskEventContext";
import { formatAsDollars } from "@/app/directories/shop/lib/formatAsDollars";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { cn } from "@frontend/shared/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover";
import { InsuranceToggle } from "./insuranceToggle";
import { PriceLoadingAnimation } from "./priceLoadingAnimation";
import { PriceToggleSymbol } from "./priceToggleSymbol";

interface PriceToggleKioskProps {
  checked: boolean;
  handlePriceModeChangeKiosk: (checked: boolean) => void;
  price: number;
  hasMultipleHrts: boolean;
  isSelfPayOnlyClient: boolean;
  isProductSelfPayOnly: boolean;
  preDiscountprice: number;
  pricesLoaded: boolean;
  priceToggleColor?: string;
  hrtIds: number[];
  size?: "normal" | "small";
}

export const PriceToggleKiosk = ({
  checked,
  handlePriceModeChangeKiosk,
  price,
  hasMultipleHrts,
  isSelfPayOnlyClient,
  isProductSelfPayOnly,
  preDiscountprice,
  pricesLoaded,
  priceToggleColor,
  hrtIds,
  size = "normal",
}: PriceToggleKioskProps) => {
  const { pricingMap, isPendingPricesMode, getShouldShowPricingLoading } =
    useKioskEvent();
  const t = useTranslations("Shop");

  const isInsurancePricingAvailable =
    isPendingPricesMode ||
    hrtIds?.some((hrtId) => {
      const priceEntry = pricingMap?.[hrtId];
      return priceEntry?.priceData.INSURED != null;
    });

  // Adding extra checks to get rid of 1 second flash of non-loading state
  const shouldShowLoading = getShouldShowPricingLoading(hrtIds, price);

  if (shouldShowLoading) {
    return <PriceLoadingAnimation label={t("CalculatingPrice")} />;
  }

  return (
    <div className={cn("w-full flex")}>
      <div className="w-full flex justify-between">
        <div className="flex flex-col gap-1">
          {checked && isPendingPricesMode ? (
            <span
              className="font-suisse text-xl font-medium text-black/40 tracking-[-0.4px]"
              data-testid="working-price"
            >
              {t("PricePending")}
            </span>
          ) : (
            <p className="flex items-center gap-2">
              {hasMultipleHrts && (
                <span
                  className={cn(
                    "font-suisse text-sm font-semibold",
                    size === "small" && "text-xs",
                  )}
                >
                  {t("StartingAt")}{" "}
                </span>
              )}
              <span
                className={cn(
                  "font-suisse text-2xl font-medium tracking-[-0.48px]",
                  size === "small" && "text-xl",
                )}
                style={{ color: priceToggleColor }}
                data-testid="working-price"
              >
                {formatAsDollars(price, t)}
              </span>
            </p>
          )}
          <span className="font-suisse text-base font-normal">
            {checked ? t("Insured") : t("SelfPay")}
          </span>
        </div>

        <InsuranceToggle
          checked={checked}
          onCheckedChange={handlePriceModeChangeKiosk}
          disabled={!isInsurancePricingAvailable}
        />
      </div>

      {!isSelfPayOnlyClient &&
        isProductSelfPayOnly &&
        pricesLoaded &&
        price !== 0 && (
          <Popover>
            <PopoverTrigger>
              <div className="bg-gradient-to-r from-[#0055FF] to-superscriptBlue w-[26px] h-[26px] flex rounded-full items-center justify-center p-[2px] cursor-pointer">
                <div className="bg-white w-full flex rounded-full items-center justify-center h-full">
                  <PriceToggleSymbol
                    isProductSelfPayOnly={isProductSelfPayOnly}
                  />
                </div>
              </div>
            </PopoverTrigger>
            <PopoverContent asChild>
              <div className="h-full flex flex-row bg-[#F3F3F3] rounded-[20px] px-4 py-2 gap-1">
                <p className="text-xs font-medium">
                  {t("InsuranceDoesNotApplyToThisProduct")}
                </p>
              </div>
            </PopoverContent>
          </Popover>
        )}

      {!!preDiscountprice && isSelfPayOnlyClient && (
        <div className="h-full flex flex-row bg-[#F3F3F3] rounded-b-[20px] px-4 py-2 gap-1">
          <p className="text-xs font-medium">{t("RegularPrice")}</p>
          <p className="text-xs font-mono">
            {formatAsDollars(preDiscountprice, t)}
          </p>
        </div>
      )}
    </div>
  );
};
